import { memo, useContext, useState, useRef } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { ObjectContext } from "@providers/ObjectProvider";
import Avatar from '@mui/joy/Avatar';
import Chip from '@mui/joy/Chip';
import Box from '@mui/joy/Box';
import Button from '@mui/joy/Button';
import ButtonGroup from '@mui/joy/ButtonGroup';
import Card from '@mui/joy/Card';
import CardContent from '@mui/joy/CardContent';
import CardOverflow from '@mui/joy/CardOverflow';
import CardActions from '@mui/joy/CardActions';
import IconButton from '@mui/joy/IconButton';
import Typography from '@mui/joy/Typography';
import SvgIcon from '@mui/joy/SvgIcon';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import UnfoldMoreRoundedIcon from '@mui/icons-material/UnfoldMoreRounded';
import UnfoldLessRoundedIcon from '@mui/icons-material/UnfoldLessRounded';

import Tooltip from '@mui/joy/Tooltip';
import Table from '@mui/joy/Table';
import Stack from '@mui/joy/Stack';
import ObjectRelationshipCard from '@components/objects/ObjectRelationshipCard';
import { gradientTint } from "@utils/gradientTint";
import { useFormContext } from "react-hook-form";
import SearchSelect from "@components/SearchSelect";
import { endpointForMorphType, morphTypeForEndpoint } from "@utils/morphTypes";
import { Checkbox, Input, Option, Select, Switch, Textarea } from "@mui/joy";
import { DeleteRowButton } from "./DeleteRowButton";
import { AddRowButton } from "./AddRowButton";
import { DatePicker } from "@components/forms/DatePicker";
import { dateFromTime, ensureDateObject, formatDateForDatabase, formatTimeForDatabase } from "@utils/dates";
import { useAuth } from "@providers/AuthProvider";
import { TimePicker } from "@components/forms/TimePicker";
import { DynamicControl } from "@components/forms/DynamicControl";
import { additionalSx } from "@utils/additionalSx";

interface BaseColumn {
  label: string;
  class?: string;


  type?: string;
  endpoint?: string;
  editable?: boolean;
  name?: string;
  options?: object[];
}

interface ColumnWithValue extends BaseColumn {
  // label: string;
  value: string;//|number|null;
  // class?: string;
  transformer?: never;
} //& CommonColumn

interface ColumnWithTransformer extends BaseColumn {
  // label: string;
  value?: never;
  // class?: string;
  transformer: Function;
}

type IColumn = ColumnWithValue | ColumnWithTransformer;

const ObjectRelationshipTableEdit = memo(function ObjectRelationshipTableEdit({
  // data = null, // parent object
  // setRelationship,
  relationship = '',//[],
  title,
  tint = null,
  sort = null,
  columns,
  collapsible = true,
  newRowData = null,
  sx = null,
  ...restProps
}) {
  const { user } = useAuth();
  const { control, register, setValue, getValues, watch } = useFormContext();
  const data = watch(relationship) ?? [];
  const [isExpanded, setIsExpanded] = useState(false);

  const perPage = 5;

  const indexedRows = data.map((row, index) => ({ row, index }));

  let viewRows = indexedRows;
  if (Array.isArray(sort)) {
    const [sortKey, sortDir] = sort;
    viewRows = [...indexedRows].sort((a, b) => {
      // pin unsaved rows to the top
      if (!!a.row.is_unsaved !== !!b.row.is_unsaved) return a.row.is_unsaved ? -1 : 1;
      const av = a.row?.[sortKey] ?? '';
      const bv = b.row?.[sortKey] ?? '';
      if (av === bv) return 0;
      return (av < bv ? -1 : 1) * (sortDir === 'desc' ? -1 : 1);
    });
  }

  const visibleRows = (isExpanded || !collapsible) ? viewRows : viewRows.slice(0, perPage);

  const canCollapse = collapsible && data.length > perPage;
  const isEmpty = data.length === 0;

  const virtualized = !!restProps.virtualized;     // opt-in, set per relationship later
  const scrollParentRef = useRef(null);
  const useWindow = virtualized && visibleRows.length > perPage;

  const rowVirtualizer = useVirtualizer({
    count: visibleRows.length,
    getScrollElement: () => scrollParentRef.current,
    estimateSize: () => 52,    // rough row height; measured per-row via measureElement
    overscan: 8,
    enabled: useWindow,
  });

  let paginationTitle = null;

  let isTableEnabled = true;
  if(restProps.hasOwnProperty('enabled')) {
    isTableEnabled = typeof restProps.enabled === 'function' ? restProps.enabled(getValues()) : restProps.enabled;
  }

  if(canCollapse/*collapsible && !isEmpty*/) {
    let titleText:any = <></>;//'All';
    if(data.length > perPage) {
      const ToggleIcon = isExpanded ? UnfoldMoreRoundedIcon : UnfoldLessRoundedIcon;
      titleText = <>
        {/* {`${isExpanded ? relationship.length : perPage} of ${relationship.length}`} */}
        <ToggleIcon sx={{width: '1rem', height: '1rem', mt: '-3px'}} />
      </>;
    }

    paginationTitle = (
      <Typography
        className="pagination"
        sx={theme => ({
          px: 1,
          py: 0.25,
          fontSize: 'sm',
          transition: 'var(--mm-default-transition)',
          backgroundColor: /*theme.palette.background.popup*/'#FFFFFF6B',// tint ? theme.palette[tint][200] : theme.palette.background.level2,
          borderRadius: 'sm',
        })}
      >
        {titleText}
      </Typography>
    );
  }

  const addRow = () => {
    let newRow = {
      is_unsaved: true,
    };

    if(newRowData !== null) {
      try {
        newRow = {
          ...newRow,
          ...newRowData({
              user,
              relationship,
              data,
              object: getValues(),
          }),
        };
      } catch(e) {
        //
      }
    }

    setValue(relationship, [newRow, ...data]);
  }

  const markRowForDeletion = (rowIndex, mark = true) => {
    // let _data = data;
    // console.log(data[rowIndex]);
    data[rowIndex].to_be_deleted = mark;

    // if it's empty, just delete it
    const { to_be_deleted, is_unsaved, ...otherProps } = data[rowIndex];
    // console.log(otherProps);
    if(data[rowIndex].is_unsaved && !Object.keys(otherProps).length) {
      data.splice(rowIndex, 1);
    }

    setValue(relationship, data);
  }

  // this is the primary value for a row w/ pivot
  const setRowValue = (rowIndex, key, value) => {
    // console.log('setRowValue', key, value);

    /*
    // for clearing
    if(data[rowIndex] === null) {
      data[rowIndex] = {
        is_unsaved: true
      }
      setValue(relationship, data);
      return;
    }
    */

   // null key is for primary objects that may have pivot data
   if(key === null) {
      const { to_be_deleted, is_unsaved, pivot } = data[rowIndex];
      data[rowIndex] = value ?? {};
      data[rowIndex].to_be_deleted = to_be_deleted;
      data[rowIndex].is_unsaved = is_unsaved;
      if(pivot && Object.keys(pivot).length) { // only set this if there's a value to set it to
        data[rowIndex].pivot = pivot;
      }
    } else {
      // check pivot
      // console.log(typeof key);
      if(~key.indexOf('pivot.')) {
        const pivotName = key.split('pivot.')[1];
        if(!data[rowIndex].pivot) {
          data[rowIndex].pivot = {};
        }

        data[rowIndex].pivot[pivotName] = value;
      }
      // standard
      else
      {
        data[rowIndex][key] = value;
      }
    }

    setValue(relationship, data);
  };

  /*
  const setRowPivotValue = (rowIndex, pivotName, value) => {
    if(!data[rowIndex].pivot) {
      data[rowIndex].pivot = {};
    }
    data[rowIndex].pivot[pivotName] = value;
    // console.log(data[rowIndex].pivot);
    setValue(relationship, data);
  };
  */

  columns = columns.filter(c => c.visibleOnEdit !== false);

  const AddAction = () => (restProps.hasOwnProperty('customAction')
    ? <>{restProps.customAction}</>
    : <AddRowButton disabled={!isTableEnabled} onClick={addRow}/>
  );

  const EmptyAddAction = () => (restProps.hasOwnProperty('customEmptyAction')
    ? <>{restProps.customEmptyAction}</>
    : <AddRowButton disabled={!isTableEnabled} onClick={addRow} includeText/>
  );


  const renderRow = ({ row, index: rowIndex }, virtualProps = {}) => {
    const isNew = !!row.is_unsaved;
    const formData = data[rowIndex] ?? null;
    const isDeleted = data[rowIndex]?.to_be_deleted ?? false;

    return (
      <tr
        key={row.id ?? `new-${rowIndex}`}
        className={`${isDeleted && 'to-delete'}`}
        {...virtualProps}
      >
        {columns.map((cell:IColumn, cellIndex:number) => {
          if(cell.hasOwnProperty('fn')) {
            cell = cell.fn({row});
          }
          // console.log(cell);
          const isEditable = isNew || cell.editableAfterSave !== false;

          let cellType = cell?.type ?? 'text';
          let cellSelectOptions = [];
          if(cell.hasOwnProperty('options')) {
            cellType = 'select';
            cellSelectOptions = typeof cell.options === 'function' ? cell.options(formData, getValues()) : cell.options;
          }
          let cellEnabled = !isDeleted;
          if(!isDeleted && cell.hasOwnProperty('enabled')) {
            cellEnabled = typeof cell.enabled === 'function' ? cell.enabled(formData, getValues()) : cell.enabled;
          }

          // console.log(cellType);

          // type: 'relationship',
          // endpoint: 'properties',

          let pivotValue = null;
          let pivotName = null;
          // console.log(cell);
          let cellName = cell?.column ?? cell?.name ?? '';
          if(cellName && cellName.indexOf('pivot.') !== -1) {
            pivotName = cellName.split('pivot.')[1];
            pivotValue = formData?.pivot?.[pivotName];// ?? null;
          }
          // console.log(pivotName);
          // const pivotValue = cell?.name?.indexOf('pivot.') !
          // const cellValue;
          //pivot.value
          if(!cellName && cell.type !== 'custom') {
            console.log(cell);

            console.warn('Empty cellName typically indicates column prop isn\'t beig passed. Check columnHelpers');
          }

          let hydrationData = [];
          const { to_be_deleted, is_unsaved, pivot, ...otherProps } = data[rowIndex];
          if(/*data[rowIndex].is_unsaved && !*/Object.keys(otherProps).length) {
            hydrationData = [data[rowIndex]];
          }

          let nameForCell = null;
          let valueForCell = null;
          if(cellName && cellName.includes('pivot.')) {
            nameForCell = cellName.split('pivot.')[1];
            valueForCell = formData?.pivot?.[pivotName];// ?? null;
          } else {
            nameForCell = cellName;
            valueForCell = formData?.[nameForCell];

            if(cellType === 'select' && cell?.multiple) {
              try {
                valueForCell = formData?.[nameForCell];//.filter(i => typeof i !== 'undefined').map(i => i.id);
              } catch(e) {
                // console.log(formData?.[nameForCell]);

              }
            }
          }

          let additionalInputProps = {};
          if(cell.hasOwnProperty('minTime')) {
            additionalInputProps.minTime = cell.minTime;
          }

          // todo: modifiers for cell values

          return (
            <td key={`${rowIndex}-${cellIndex}`} className={cell?.class}>
              {isEditable === true && (<>
                {/*
                <Controller
                  name={name}
                  control={control}
                  // rules={{ required: true }}
                  render={({ field: {ref, ...fieldProps} }) => (
                    <SearchSelect
                      {...fieldProps}
                      endpoint={restProps.endpoint}
                      hydrateWith={hydrateWith}
                      // hydrateWith={getValues().spouse ? [getValues().spouse] : []}
                    />
                  )}
                />
                */}
                {cellType === 'relationship' && (
                    <SearchSelect
                      key={cellIndex}
                      // {...fieldProps}
                      endpoint={cell.endpoint}
                      include={cell?.include}
                      disabled={!cellEnabled}
                      onChange={(val, objVal) => setRowValue(rowIndex, null, objVal)}
                      value={formData?.id ?? ''}
                      groupBy={cell?.groupBy}
                      // hydrateWith={hydrateWith}
                      hydrateWith={hydrationData}
                    />
                )}
                {cellType === 'polymorphic' && (
                  <SearchSelect
                    key={`${cellIndex}-polymorphic`}
                    endpoint={(cell.endpoints ?? []).join(',')}
                    include={cell?.include}
                    disabled={!cellEnabled}
                    onChange={(val, objVal) => {
                      if(!val) {
                        setRowValue(rowIndex, cellName, null);
                        setRowValue(rowIndex, cell.typeColumn, null);
                        setRowValue(rowIndex, cell.relationship, null);
                        return;
                      }

                      const morphType = morphTypeForEndpoint(objVal?.__endpoint ?? cell.endpoints?.[0]);

                      if(!morphType) {
                        return;
                      }

                      setRowValue(rowIndex, cellName, val);
                      setRowValue(rowIndex, cell.typeColumn, morphType);
                      setRowValue(rowIndex, cell.relationship, objVal ?? null);
                    }}
                    value={formData?.[cellName] ?? ''}
                    valueEndpoint={endpointForMorphType(formData?.[cell.typeColumn])}
                    hydrateWith={
                      formData?.[cell.relationship]
                        ? [{...formData[cell.relationship], __endpoint: endpointForMorphType(formData?.[cell.typeColumn])}]
                        : []
                    }
                  />
              )}
                {cellType === 'select' && (
                  <Select
                    key={cellIndex}
                    variant="outlined"
                    placeholder={cell?.placeholder ?? "Choose..."}
                    disabled={!cellEnabled}
                    multiple={cell?.multiple}
                    renderValue={cell?.renderValue}
                    value={valueForCell ?? (cell?.multiple ? [] : '')}
                    onChange={(e, v) => {
                      if(typeof cell.onChange === 'function') {
                        cell.onChange({object: getValues(), value: v, rowIndex, setRowValue});
                      } else {
                        setRowValue(rowIndex, cellName, v)
                      }
                    }}
                    // value={}
                    // id={name}
                    // {...register(name, config)}
                    // defaultValue={defaultValue}
                    // {...restProps}
                    // name={name}
                  >
                    {cellSelectOptions.map((o, index) => (
                      <Option key={index} value={cell?.objectAsValue ? o : (o.hasOwnProperty('id') ? o.id : o.value)}>
                        {cell?.multiple && (
                          <Checkbox checked={Array.isArray(valueForCell) && valueForCell.indexOf(o.id) !== -1}/>
                        )}
                        {o?.name ?? o?.address ?? o?.unit ?? o.label}
                      </Option>
                    ))}
                  </Select>
                )}
                {cellType === 'boolean' && (
                  <Typography sx={{userSelect: 'none',}} component="label" startDecorator={
                    <Switch
                      key={cellIndex}
                      variant={cellEnabled ? 'solid' : 'outlined'}
                      disabled={!cellEnabled}
                      // id={name}
                      // type={type}
                      // {...register(name, config)}

                      // defaultValue={defaultValue}
                      // {...onChange}
                      onChange={(e) => {
                        // setValue(name, !(!!value))
                        // console.log(e.target.value)
                        setRowValue(rowIndex, cellName, !valueForCell ? 1 : 0)

                        if(typeof cell.onChange === 'function') {
                          cell.onChange(formData, data, (_cell, val) => setRowValue(rowIndex, _cell, val));
                        }
                      }}
                      checked={!!valueForCell}
                      // checked={restProps?.reverse ? !(!!value) : !!value}
                    />
                  }>
                    {cell.label}
                    {/* {...restProps.inputLabel ?? ''} */}
                  </Typography>
                )}
                {cellType === 'date' && (
                  <Input
                    key={cellIndex}
                    variant="outlined"
                    placeholder={cell?.placeholder ?? `${cell.label}...`}
                    disabled={!cellEnabled}
                    value={valueForCell ? valueForCell.toString().slice(0, 10) : ''}
                    onChange={(e) => /*setRowPivotValue*/ setRowValue(rowIndex, cellName, e.target.value)}
                    type={'date'}
                    // {...(cell?.inputType === 'number') ? {step: 'any'} : {}}
                    startDecorator={cell?.startDecorator}
                    endDecorator={cell?.endDecorator}
                  />
                )}
                {cellType === 'date-disabled' && (
                  <DatePicker
                    key={cellIndex}
                    formatDensity="spacious"
                    // defaultValue={new Date()}
                    value={valueForCell ? ensureDateObject(valueForCell) : null}
                    // onChange={(e) => /*setRowPivotValue*/ setRowValue(rowIndex, pivotName, e.target.value)}
                    onChange={(value) => {
                      /*setRowPivotValue*/ setRowValue(rowIndex, cellName, value.target.value /*formatDateForDatabase(value)*/)
                    }}
                    // {...fieldProps}
                  />
                )}
                {cellType === 'time' && (
                  <TimePicker
                    key={cellIndex}
                    formatDensity="spacious"
                    // defaultValue={new Date()}
                    value={valueForCell ? dateFromTime(valueForCell) : null}
                    // onChange={(e) => /*setRowPivotValue*/ setRowValue(rowIndex, pivotName, e.target.value)}
                    onChange={(value) => {
                      if(value) {
                        value = formatTimeForDatabase(value);
                      }
                      /*setRowPivotValue*/ setRowValue(rowIndex, cellName, value)
                    }}
                    {...additionalInputProps}
                    // {...fieldProps}
                  />
                )}
                {cellType === 'text' && (
                  <Input
                    key={cellIndex}
                    variant="outlined"
                    placeholder={cell?.placeholder ?? `${cell.label}...`}
                    disabled={!cellEnabled}
                    value={valueForCell ?? ''}
                    onChange={(e) => /*setRowPivotValue*/ setRowValue(rowIndex, cellName, e.target.value)}
                    type={cell?.inputType ?? 'text'}
                    {...(cell?.inputType === 'number') ? {step: 'any'} : {}}
                    startDecorator={cell?.startDecorator}
                    endDecorator={cell?.endDecorator}
                  />
                )}
                {cellType === 'textarea' && (
                  <Textarea
                    key={cellIndex}
                    variant="outlined"
                    minRows={1}
                    maxRows={3}
                    // key={`${rowIndex}-note`}
                    placeholder={cell?.placeholder ?? `${cell.label}...`}
                    disabled={!cellEnabled}
                    value={valueForCell ?? ''}
                    onChange={(e) => setRowValue(rowIndex, cellName, e.target.value)}
                  />
                )}
                {cellType === 'custom' && (<>
                  {cell?.render?.edit({rowIndex, row, data, setRowValue})}
                </>)}
              </>)}
              {isEditable !== true && (<>
                {cell.value && row[cell.value]}
                {cell?.render?.editDisabled && cell.render.editDisabled(row, data)}
              </>)}
            </td>
          )
        })}
        <td className="min">
          <DeleteRowButton data={data} index={rowIndex} toggle={markRowForDeletion}/>
          {typeof restProps.additionalActions !== 'undefined' && restProps.additionalActions({row, data, getValues, setValue})}
        </td>
      </tr>
    );
  };


  return (
    <ObjectRelationshipCard
      unpadded
      // empty={isEmpty}
      title={
        <CardOverflow
          sx={theme => ({
            borderTopLeftRadius: 'calc(var(--Card-childRadius) + var(--variant-borderWidth))',
            borderTopRightRadius: 'calc(var(--Card-childRadius) + var(--variant-borderWidth))',
            marginBlockStart: 'var(--CardOverflow-offset)',
            ...gradientTint(theme, tint),
          })}
        >
          <CardActions
            sx={theme => ({
              py: 0,//1.5,
              // pointerEvents: canCollapse ? 'all' : 'none',

              userSelect: 'none',
              ':hover .pagination': {
                backgroundColor: '#FFFFFFB8',
              },
            })}
          >
            <Stack
              direction="row"
              justifyContent="flex-start"
              alignItems="center"
              spacing={3}
              width={'100%'}
            >
              <Stack
                direction="row"
                justifyContent="flex-start"
                alignItems="center"
                spacing={0.5}
                width={'100%'}
                sx={{
                  pointerEvents: canCollapse ? 'all' : 'none',
                  py: 1.5,
                  cursor: 'pointer',//canCollapse ? 'pointer' : 'text',
                }}
                onClick={() => setIsExpanded(!isExpanded)}
              >
                <Typography sx={theme => ({fontWeight: '600', color: 'white' })}>{title}</Typography>
                {paginationTitle}
              </Stack>

              {/* {...restProps.customAction && ...restProps.customAction()} */}
              {/* <AddRowButton disabled={!isTableEnabled} onClick={addRow}/> */}
              <AddAction/>
            </Stack>
          </CardActions>
        </CardOverflow>
      }
      sx={theme => ({
        '--mm-card-height': isExpanded ? 'auto' : '250px',
        // ...(isExpanded ? {'--mm-card-height': 'auto'} : {}),
        // ...sx
        ...additionalSx(theme, sx),
      })}
    >
      {restProps?.warning}
      {/* <CardOverflow> */}
      <div ref={scrollParentRef} style={useWindow ? { maxHeight: 420, overflowY: 'auto', overflowX: 'hidden' } : undefined}>
      <Table
        stickyHeader
        stripe="odd"
        // hoverRow
        sx={{
          margin: 0,
          padding: 0,
          tableLayout: 'auto',
          'thead th': {
            borderRadius: '0 !important',
          },
          'tbody td': {
            // borderWidth: '0 !important',
            verticalAlign: 'top',
          },
        }}
      >
        <thead>
          <tr>
            {columns.map((header:IColumn) => {
              if(header.hasOwnProperty('fn')) {
                header = header.fn({row:{}});
              }
              return <th key={header.label} style={{width: header?.width ?? `${100 / columns.length}%`}}>{header.label}</th>
            })}
            <th className="min"></th>
          </tr>
        </thead>
        <tbody>
          {!isTableEnabled && restProps?.disabledMessage && (
            <tr>
              <td colSpan={columns.length+1}>
                {restProps.disabledMessage}
              </td>
            </tr>
          )}
          {isEmpty && (
            <tr>
              <td colSpan={columns.length+1}>
                <EmptyAddAction/>
              </td>
            </tr>
          )}
          {useWindow ? (() => {
            const items = rowVirtualizer.getVirtualItems();
            const padTop = items.length ? items[0].start : 0;
            const padBottom = items.length ? rowVirtualizer.getTotalSize() - items[items.length - 1].end : 0;
            return (
              <>
                {padTop > 0 && (
                  <tr key="pad-top" aria-hidden>
                    <td colSpan={columns.length + 1} style={{ height: padTop, padding: 0, border: 0 }} />
                  </tr>
                )}
                {items.map((vi) => renderRow(visibleRows[vi.index], {
                  ref: rowVirtualizer.measureElement,
                  'data-index': vi.index,
                }))}
                {padBottom > 0 && (
                  <tr key="pad-bottom" aria-hidden>
                    <td colSpan={columns.length + 1} style={{ height: padBottom, padding: 0, border: 0 }} />
                  </tr>
                )}
              </>
            );
          })() : (
            visibleRows.map((vr) => renderRow(vr))
          )}
        </tbody>
      </Table>
      </div>
      {/* </CardOverflow> */}

    </ObjectRelationshipCard>
  );
});

export default ObjectRelationshipTableEdit;